import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useFilterOptions, type FilterOption } from "../hooks/useReportFilters";
import { paymentMethodLabel, rangeText } from "../lib/labels";
import {
	DATE_PRESETS,
	countExtraFilters,
	getDateRangeFromPreset,
	toYmd,
	type DateRangePreset,
	type ReportFilterField,
	type ReportFiltersState,
} from "../types";

/** Payment methods offered when the company's own list cannot be read. */
const FALLBACK_PAYMENT_CODES = ["cash", "card", "bank_transfer", "credit"];

interface ReportsFiltersBarProps {
	value: ReportFiltersState;
	onChange: (state: ReportFiltersState) => void;
	/** Filters the current report honors; the others are not shown. */
	fields: ReportFilterField[];
	/** Extra control at the end of the first row (e.g. report search on the reports home). */
	trailing?: React.ReactNode;
	/** Line under the bar, e.g. "compared with …". */
	note?: React.ReactNode;
	className?: string;
}

/**
 * One compact bar for every report: period chips first (the filter people change most), then only the
 * dimensions this report honors. On phones the dimensions fold behind a "Filters" button.
 */
export function ReportsFiltersBar({ value, onChange, fields, trailing, note, className }: ReportsFiltersBarProps) {
	const { t } = useTranslation();
	const [showMore, setShowMore] = useState(false);
	const options = useFilterOptions(fields, value.branchId);
	const hasPeriod = fields.includes("period");
	const range = getDateRangeFromPreset(value);
	const extraCount = countExtraFilters(value, fields);

	const setPreset = (preset: DateRangePreset) => {
		if (preset !== "custom") {
			onChange({ ...value, dateRangePreset: preset, customDateFrom: "", customDateTo: "" });
			return;
		}
		// Custom starts from the range being shown, so the dates are never blank.
		const shown = getDateRangeFromPreset(value);
		const today = toYmd(new Date());
		onChange({
			...value,
			dateRangePreset: "custom",
			customDateFrom: value.customDateFrom || shown.startDate || today,
			customDateTo: value.customDateTo || shown.endDate || today,
		});
	};

	const paymentOptions: FilterOption[] = (
		options.paymentMethodCodes.length ? options.paymentMethodCodes : FALLBACK_PAYMENT_CODES
	).map(code => ({ value: code, label: paymentMethodLabel(t, code) }));

	const dimensions: {
		field: ReportFilterField;
		key: keyof ReportFiltersState;
		label: string;
		allLabel: string;
		options: FilterOption[];
		/** A choice between one option is no choice (single-branch stores). */
		minOptions: number;
		/** Picking this one clears filters that depend on it. */
		resets?: (keyof ReportFiltersState)[];
	}[] = [
		{
			field: "branch",
			key: "branchId",
			label: t("reports.filters.branch"),
			allLabel: t("reports.filters.allBranches"),
			options: options.branch,
			minOptions: 2,
			resets: ["posId", "employeeId"],
		},
		{
			field: "pos",
			key: "posId",
			label: t("reports.filters.pos"),
			allLabel: t("reports.filters.allPos"),
			options: options.pos,
			minOptions: 2,
		},
		{
			field: "employee",
			key: "employeeId",
			label: t("reports.filters.employee"),
			allLabel: t("reports.filters.allEmployees"),
			options: options.employee,
			minOptions: 1,
		},
		{
			field: "category",
			key: "categoryId",
			label: t("reports.filters.category"),
			allLabel: t("reports.filters.allCategories"),
			options: options.category,
			minOptions: 1,
		},
		{
			field: "paymentMethod",
			key: "paymentMethod",
			label: t("reports.filters.paymentMethod"),
			allLabel: t("reports.filters.allPaymentMethods"),
			options: paymentOptions,
			minOptions: 1,
		},
	];
	const visibleDimensions = dimensions.filter(
		d => fields.includes(d.field) && (d.options.length >= d.minOptions || Boolean(value[d.key])),
	);

	const activeDimensions = dimensions
		.filter(d => fields.includes(d.field) && value[d.key])
		.map(d => ({
			field: d.field,
			label: d.label,
			valueLabel: d.options.find(o => o.value === value[d.key])?.label ?? `#${value[d.key]}`,
		}));

	const clearDimensions = () =>
		onChange({ ...value, branchId: "", posId: "", employeeId: "", categoryId: "", paymentMethod: "" });

	return (
		<section
			aria-label={t("reports.filters.title")}
			className={cn(
				"rounded-2xl border bg-card p-3 shadow-sm sm:p-4 print:border-0 print:p-0 print:shadow-none",
				className,
			)}
		>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-center print:hidden">
				{hasPeriod && (
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<CalendarDays
							className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block"
							aria-hidden="true"
						/>
						<div
							role="radiogroup"
							aria-label={t("reports.filters.period")}
							className="-mx-1 flex min-w-0 gap-1.5 overflow-x-auto px-1 pb-1 lg:flex-wrap lg:overflow-visible lg:pb-0"
						>
							{[...DATE_PRESETS, "custom" as const].map(preset => {
								const active = value.dateRangePreset === preset;
								return (
									<button
										key={preset}
										type="button"
										role="radio"
										aria-checked={active}
										onClick={() => setPreset(preset)}
										className={cn(
											"h-9 shrink-0 whitespace-nowrap rounded-full border px-3.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
											active
												? "border-primary bg-primary text-primary-foreground"
												: "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
										)}
									>
										{t(`reports.dateRanges.${preset}`)}
									</button>
								);
							})}
						</div>
					</div>
				)}

				{(visibleDimensions.length > 0 || trailing) && (
					<div className="flex items-center gap-2">
						{visibleDimensions.length > 0 && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="h-9 shrink-0 md:hidden"
								aria-expanded={showMore}
								onClick={() => setShowMore(v => !v)}
							>
								<SlidersHorizontal className="h-4 w-4 me-2" />
								{t("reports.filters.title")}
								{extraCount > 0 && (
									<span className="ms-2 rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
										{extraCount}
									</span>
								)}
							</Button>
						)}
						{trailing}
					</div>
				)}
			</div>

			{hasPeriod && value.dateRangePreset === "custom" && (
				<div className="mt-3 flex flex-wrap items-center gap-2 print:hidden">
					<label className="flex items-center gap-2 text-sm text-muted-foreground">
						{t("reports.dateRange.fromDate")}
						<Input
							aria-label={t("reports.dateRange.fromDate")}
							type="date"
							value={value.customDateFrom}
							max={value.customDateTo || undefined}
							onChange={e => onChange({ ...value, customDateFrom: e.target.value })}
							className="h-9 w-[160px] text-foreground"
						/>
					</label>
					<label className="flex items-center gap-2 text-sm text-muted-foreground">
						{t("reports.dateRange.toDate")}
						<Input
							aria-label={t("reports.dateRange.toDate")}
							type="date"
							value={value.customDateTo}
							min={value.customDateFrom || undefined}
							onChange={e => onChange({ ...value, customDateTo: e.target.value })}
							className="h-9 w-[160px] text-foreground"
						/>
					</label>
				</div>
			)}

			{visibleDimensions.length > 0 && (
				<div
					className={cn(
						"mt-3 grid-cols-1 gap-2 border-t pt-3 sm:grid-cols-2 md:grid md:grid-cols-3 xl:grid-cols-5 print:hidden",
						showMore ? "grid" : "hidden",
					)}
				>
					{visibleDimensions.map(d => (
						<div key={d.field} className="space-y-1">
							<span className="text-xs text-muted-foreground" id={`report-filter-${d.field}`}>
								{d.label}
							</span>
							<Select
								value={(value[d.key] as string) || "all"}
								onValueChange={v => {
									const next = { ...value, [d.key]: v === "all" ? "" : v };
									for (const k of d.resets ?? []) (next as Record<string, string>)[k] = "";
									onChange(next);
								}}
							>
								<SelectTrigger
									className={cn("h-10", value[d.key] && "border-primary/50 bg-primary/5")}
									aria-labelledby={`report-filter-${d.field}`}
								>
									<SelectValue placeholder={d.allLabel} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{d.allLabel}</SelectItem>
									{d.options.map(o => (
										<SelectItem key={o.value} value={o.value}>
											{o.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					))}
				</div>
			)}

			{(hasPeriod || note || activeDimensions.length > 0) && (
				<div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground print:mt-0">
					{hasPeriod && (
						<span>
							{t("reports.dateRange.showingDataFor")}:{" "}
							<strong className="font-semibold text-foreground">
								{value.dateRangePreset === "all"
									? t("reports.dateRanges.all")
									: rangeText(range) || t("reports.dateRange.pickDates")}
							</strong>
						</span>
					)}
					{/* Applied filters in words: visible on phones (where the selects fold away) and in print. */}
					{activeDimensions.map(d => (
						<span key={d.field}>
							{d.label}: <strong className="font-semibold text-foreground">{d.valueLabel}</strong>
						</span>
					))}
					<span className="print:hidden">{note}</span>
					{extraCount > 0 && (
						<button
							type="button"
							onClick={clearDimensions}
							className="inline-flex items-center gap-1 rounded text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring print:hidden"
						>
							<X className="h-3.5 w-3.5" aria-hidden="true" />
							{t("reports.filters.clear", { count: extraCount })}
						</button>
					)}
				</div>
			)}
		</section>
	);
}
